import React, { useMemo } from 'react';
import Box from 'ui/Box';
import { useSelector } from 'react-redux';
import { getMarketProducts, getSelectedProduct } from 'ducks/products';
import { ProductType } from '@inter-ikea-storageone/range';
import styled from 'styled-components';
import { ProductThumb } from 'selectedMenu/components';
import Body from 'ui/Typography/Body';
import { useCore } from 'contexts/core';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import KompisToolTip from 'ui/KompisToolTip/KompisToolTip';
import deskImg from './images/desk.jpg';
import changingTableImg from './images/changingTable.jpg';
import { useBehaviorTracker } from 'contexts/behaviors';

const Thumbnails = styled.ul`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;
  margin: 0;
  padding-left: 0;
`;

const Image = styled.img`
  width: ${({ theme }) => theme.space.xxxl};
  height: ${({ theme }) => theme.space.xxxxl};
  padding-bottom: ${({ theme }) => theme.space.xs};
  display: flex;
  justify-content: center;
`;

const StyledButton = styled.button`
  position: relative;
  background: #fff;
  cursor: pointer;
  width: 100%;
  border: none;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0), 0 0 0 rgba(0, 0, 0, 0);

  @media (hover: hover) {
    &:hover {
      box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1), 0 8px 8px rgba(0, 0, 0, 0.04);
    }
  }
  &:focus {
    outline: 0.125rem solid ${({ theme }) => theme.colors.feedback.focus};
  }
`;

const ChangeChangingTableDeskFunctionality = ({ ...props }) => {
  const coreApi = useCore();
  const behaviorTracker = useBehaviorTracker();
  const selectedProduct = useSelector(getSelectedProduct);
  const products = useSelector(getMarketProducts);
  const selectedEntity = coreApi.entities.findSelectedEntity();

  const changingTableProduct = useMemo(() => {
    return products.find(p => p.type === ProductType.SMASTAD_CHANGING_TABLE_W_DRAWERS_ASM);
  }, [products]);

  const deskProduct = useMemo(() => {
    return products.find(p => p.type === ProductType.SMASTAD_DESK_W_DRAWERS_ASM);
  }, [products]);

  if (!selectedProduct) {
    return null;
  }
  const selectedFunction =
    selectedProduct.type === ProductType.SMASTAD_CHANGING_TABLE
      ? changingTableProduct?.typeName
      : deskProduct?.typeName;

  const onSelect = (option: ProductType) =>
    behaviorTracker.custom(() => {
      let placementResult;
      const newProductCoreId =
        option === ProductType.SMASTAD_CHANGING_TABLE_W_DRAWERS_ASM
          ? changingTableProduct?.coreProductId
          : deskProduct?.coreProductId;
      if (selectedEntity && newProductCoreId) {
        placementResult = coreApi.products.removeAndAdd(selectedEntity.id, newProductCoreId);
      }
      if (placementResult?.valid && placementResult.placedEntity) {
        coreApi.entities.select(placementResult.placedEntity);
      }
    });

  return (
    <Box>
      <Body mb="s" type={ThemeFontStyleTypeEnum.bodyMedium}>
        {selectedFunction}
      </Body>
      <Thumbnails {...props}>
        <ProductThumb
          data-testid={'changingTable'}
          onClick={() => onSelect(ProductType.SMASTAD_CHANGING_TABLE_W_DRAWERS_ASM)}
          data-selected={selectedProduct.type === ProductType.SMASTAD_CHANGING_TABLE}
        >
          <StyledButton>
            <KompisToolTip text={changingTableProduct?.typeName || ''} uniqueId="changingTable">
              <Image src={changingTableImg} alt={changingTableProduct?.typeName} />
            </KompisToolTip>
          </StyledButton>
        </ProductThumb>
        <ProductThumb
          data-testid="desk"
          onClick={() => onSelect(ProductType.SMASTAD_DESK_W_DRAWERS_ASM)}
          data-selected={selectedProduct.type === ProductType.SMASTAD_DESK}
        >
          <StyledButton>
            <KompisToolTip text={deskProduct?.typeName || ''} uniqueId="desk">
              <Image src={deskImg} alt={deskProduct?.typeName} />
            </KompisToolTip>
          </StyledButton>
        </ProductThumb>
      </Thumbnails>
    </Box>
  );
};

export default ChangeChangingTableDeskFunctionality;
